import React, {
  isValidElement,
  useCallback,
  useMemo,
  useReducer,
  useState,
} from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import FableticsModal from '~/shared/components/FableticsModal';
import StyleContext from '~/shared/components/StyleContext';
import { ProductFPL } from '~/shared/constants/products';
import { getAvailableProductQuantity } from '~/shared/utils/getAvailableProductQuantity';
import { useMembership } from '~/techstyle-shared/react-accounts';
import { useCart, useCartActions } from '~/techstyle-shared/react-cart';
import {
  Button,
  Carousel,
  Heading,
  ProductDetail,
  mobile,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';
import { useProductsByMasterProductIdsQuery } from '~/techstyle-shared/react-products';
import { usePromos } from '~/techstyle-shared/react-promos';

import WearItWithItem from './WearItWithItem';

function findPromoByFplId(promos, fplId) {
  return promos.find((promo) => promo.featuredProductLocationId === fplId);
}

const WearItWithWrapper = styled.div`
  margin-bottom: 28px;
  ${mobile`
    padding: 0 12px;
  `}
`;

const PromoTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const CarouselStyled = styled(Carousel)`
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet.maxWidth}px) {
    display: block;
  }

  & .flickity-prev-next-button,
  .flickity-prev-next-button:disabled {
    background-color: white;
    border: 1px solid ${({ theme }) => theme.colors.upsellCarouselBorder};
    box-shadow: 0px 8px 10px -5px ${({ theme }) => theme.colors.upsellCarouselBorder};
    color: ${({ theme }) => theme.colors.iconColor};
    opacity: 1;

    &.previous {
      left: -22px;
    }

    &.next {
      right: -22px;
    }
  }

  & .flickity-prev-next-button:disabled {
    display: none;
  }

  & .flickity-page-dots {
    align-items: center;
    display: flex;
    justify-content: center;

    & .dot {
      height: 8px;
      margin: 0 6px;
      width: 8px;

      &.is-selected {
        background: ${({ theme }) => theme.colors.backgroundDark};
        height: 10px;
        width: 10px;
      }
    }

    bottom: -24px;

    ${mobile`
      bottom: -16px;
    `}
  }

  & [data-carousel-cell] {
    ${mobile`
      margin-right: 24px;
      width: 350px;
    `}

    @media (max-width: 400px) {
      margin-right: 12px;
      width: 300px;
    }

    @media (max-width: 350px) {
      width: 260px;
    }
  }
`;

const PromoSavingsLabel = styled.span`
  color: ${({ theme }) => theme.colors.textEmphasized};
`;

const modalDialogStyle = css`
  background-color: ${({ theme }) => theme.colors.palette.white};
  border-radius: 16px;
  padding: 32px 22px;
  margin: 0 20px;
  max-width: 550px;
`;

const ModalContent = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.sublabelBlack};
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  width: 100%;
`;

const ModalHeading = styled(Heading)`
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 8px;
  text-align: center;
`;

const TermsMessage = styled.div`
  font-size: 16px;
  margin-bottom: 24px;
  text-align: center;
`;

const TermsButton = styled(Button)`
  background: ${({ invertColor, theme }) =>
    invertColor
      ? theme.colors.palette.white
      : theme.colors.wearItWithTermsButton};
  border-radius: 5px;
  color: ${({ invertColor, theme }) =>
    invertColor
      ? theme.colors.wearItWithTermsButton
      : theme.colors.palette.white};
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  padding: 12px 32px;
  text-align: center;

  &:not(:last-child) {
    margin-bottom: 16px;
  }

  ${mobile`
    width: 100%;
  `}
`;

const reducer = (state, action) => {
  switch (action.type) {
    case 'Add':
      return {
        ...state,
        [action.product.masterProductId]: action.product,
      };
    case 'Remove': {
      const newState = { ...state };
      delete newState[action.product.masterProductId];
      return newState;
    }
    default:
      return state;
  }
};

export default function WearItWith({ children, wearItWithProductIds }) {
  const { promos } = usePromos();
  const cart = useCart({ isLoginRequired: true });
  const cartActions = useCartActions();
  const { isMobile } = useBreakpoint();
  const [addToCartItems, dispatch] = useReducer(reducer, []);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const { loading, data, error } = useProductsByMasterProductIdsQuery({
    productIds: wearItWithProductIds,
  });

  const { isVip } = useMembership();

  const availableWIWProducts = useMemo(() => {
    return (
      data?.masterProducts.filter(
        (product) =>
          getAvailableProductQuantity(product) > 0 &&
          product.featuredProductLocationIdList.includes(
            ProductFPL.completeTheLookItem
          )
      ) || []
    );
  }, [data]);

  const completeTheLookPromo = findPromoByFplId(
    promos,
    ProductFPL.completeTheLookOutfit
  );
  const completeTheLookItemPromo = findPromoByFplId(
    promos,
    ProductFPL.completeTheLookItem
  );

  const cartExceedsMaxCTLItems = useMemo(
    () =>
      cart.items.filter((item) =>
        item.fplIds?.includes(ProductFPL.completeTheLookItem)
      ).length >= completeTheLookPromo?.maxProductQuantity,
    [cart.items, completeTheLookPromo]
  );

  const addCTLItemsToCart = useCallback(() => {
    Object.values(addToCartItems).forEach((cartItem) =>
      cartActions.addItemToCart(cartItem.productId)
    );
    setModalIsOpen(false);
  }, [addToCartItems, cartActions]);

  const onSuccessfulAddToCart = useCallback(() => {
    if (cartExceedsMaxCTLItems) {
      setModalIsOpen(true);
    } else {
      addCTLItemsToCart();
    }
  }, [addCTLItemsToCart, cartExceedsMaxCTLItems]);

  const possibleChild = useMemo(
    () =>
      isValidElement(children)
        ? React.cloneElement(children, {
            onSuccessfulAddToCart,
          })
        : null,
    [children, onSuccessfulAddToCart]
  );

  if (
    error ||
    loading ||
    !wearItWithProductIds ||
    !wearItWithProductIds.length ||
    !availableWIWProducts.length ||
    !completeTheLookPromo
  ) {
    return children;
  }

  const hasMultipleSlides = availableWIWProducts.length > 1;
  const discountLabel = completeTheLookPromo.discounts[0]?.label;
  const numberOfDiscountItems = completeTheLookPromo.maxProductQuantity;

  return (
    <>
      {modalIsOpen ? (
        <FableticsModal
          dialogStyle={modalDialogStyle}
          isOpen={modalIsOpen}
          overlayClickExits
          titleId="cartHasMaxCTLItems"
        >
          <ModalContent>
            <ModalHeading>
              <FormattedMessage
                id="site_pdp.ctl_modal_heading"
                defaultMessage="Heads up!"
              />
            </ModalHeading>
            <TermsMessage>
              <FormattedMessage
                id="site_pdp.ctl_modal_copy"
                defaultMessage="You're adding more products than are allowed with this promotion - they'll be added to your cart without the discount."
              />
            </TermsMessage>
            <TermsButton variant="unstyled" onClick={addCTLItemsToCart}>
              <FormattedMessage
                id="site_pdp.ctl_modal_button_accept"
                defaultMessage="Okay, sounds good!"
              />
            </TermsButton>
            <TermsButton
              variant="unstyled"
              invertColor
              onClick={() => setModalIsOpen(false)}
            >
              <FormattedMessage
                id="site_pdp.ctl_modal_button_cancel"
                defaultMessage="Go back"
              />
            </TermsButton>
          </ModalContent>
        </FableticsModal>
      ) : null}
      <StyleContext context="errorRegistrationDisabled">
        <WearItWithWrapper>
          <PromoTitle>
            <FormattedMessage
              id="site_pdp.ctl_module_copy"
              defaultMessage="Add up to {1} {1, plural, one {item} other {items}} to your outfit, {2}"
              values={{
                1: numberOfDiscountItems,
                2: <PromoSavingsLabel>{discountLabel}</PromoSavingsLabel>,
              }}
            />
          </PromoTitle>
          <CarouselStyled
            cellAlign={isMobile ? 'left' : 'center'}
            contain
            draggable={hasMultipleSlides}
            freeScroll={false}
            overlayClickExits
            pageDots={hasMultipleSlides}
            prevNextButtons={isMobile ? false : hasMultipleSlides}
            progressBarAutoTag="pdp_media_carousel_progressbar"
            progressBarHeight={4}
          >
            {availableWIWProducts.map((wearItWithProduct) => {
              const hasAvailableProducts = wearItWithProduct.skus.some(
                (sku) => sku.availableQuantity !== 0
              );

              if (!hasAvailableProducts) {
                return null;
              }
              return (
                <ProductDetail
                  key={wearItWithProduct.masterProductId}
                  product={wearItWithProduct}
                >
                  <WearItWithItem
                    isInAddToCartItems={Boolean(
                      addToCartItems[wearItWithProduct.masterProductId]
                    )}
                    isVip={isVip}
                    promoCode={completeTheLookItemPromo.code}
                    updateAddToCartDispatch={dispatch}
                  />
                </ProductDetail>
              );
            })}
          </CarouselStyled>
        </WearItWithWrapper>
      </StyleContext>
      {possibleChild}
    </>
  );
}

WearItWith.propTypes = {
  children: PropTypes.node,
  wearItWithProductIds: PropTypes.arrayOf(PropTypes.number),
};
